/**
 * Copia automática (lógica pura, testeable).
 *
 * La primera vez se elige dónde se guardan las copias («Dentro de Rumbo», en
 * el propio móvil, o una carpeta si el navegador deja escribir en ella) y
 * cada cuánto (cada día o cada semana). Desde entonces, al abrir Inicio, si
 * ya toca, la copia se hace sola. La elección es de este dispositivo y este
 * presupuesto (preferencia local `copia.auto`); el día de la última copia
 * automática, también (`copia.autoUltima`).
 *
 * Un presupuesto que está también en la nube no necesita esto: vive en el
 * servidor, que guarda cada noche una copia cifrada aparte.
 */
import { diaLocal, diasDesde } from './copia';

export type DestinoCopia = 'rumbo' | 'carpeta';
export type FrecuenciaCopia = 'dia' | 'semana';

export type ConfigCopiaAuto = {
  destino: DestinoCopia;
  frecuencia: FrecuenciaCopia;
};

/** Copias que se guardan «Dentro de Rumbo» por presupuesto. */
export const MAX_COPIAS_INTERNAS = 10;

export function diasDeFrecuencia(frecuencia: FrecuenciaCopia): number {
  return frecuencia === 'dia' ? 1 : 7;
}

/** Lee la preferencia (o un valor viejo o roto) como configuración válida. */
export function leerConfigCopia(valor: unknown): ConfigCopiaAuto | null {
  if (!valor || typeof valor !== 'object') {
    return null;
  }
  const { destino, frecuencia } = valor as Partial<ConfigCopiaAuto>;
  if (destino !== 'rumbo' && destino !== 'carpeta') {
    return null;
  }
  return {
    destino,
    frecuencia: frecuencia === 'semana' ? 'semana' : 'dia',
  };
}

/** ¿Toca hacer la copia automática hoy? */
export function tocaCopiaAuto(
  config: ConfigCopiaAuto | null,
  ultimaAuto: string | null | undefined,
  hoy: string,
): boolean {
  if (!config) {
    return false;
  }
  const dias = diasDesde(ultimaAuto, hoy);
  return dias === null || dias >= diasDeFrecuencia(config.frecuencia);
}

/** El más reciente de dos días `AAAA-MM-DD` (cualquiera puede faltar). */
export function diaMasReciente(
  a: string | null | undefined,
  b: string | null | undefined,
): string | null {
  if (!a) {
    return b || null;
  }
  if (!b) {
    return a;
  }
  return a > b ? a : b;
}

/**
 * Estado del ancla de Inicio:
 * - `nube`: el presupuesto está también en la nube (siempre tranquila);
 * - `bien`: la última copia está dentro del plazo;
 * - `pronto`: se pasó el plazo, o la última automática falló (ámbar);
 * - `mal`: no hay ninguna copia, o hace mucho de la última (rojo).
 */
export type NivelAncla = 'nube' | 'bien' | 'pronto' | 'mal';

export type EstadoAncla = {
  nivel: NivelAncla;
  /** 0..1: cuánto se ha llenado el círculo hacia la siguiente copia. */
  fraccion: number;
  /** Días desde la última copia (`null` = nunca). */
  dias: number | null;
  /** ¿Falta elegir dónde guardar las copias? */
  sinElegir: boolean;
};

export function estadoAncla({
  enNube,
  config,
  ultima,
  fallo,
  hoy,
}: {
  enNube: boolean;
  config: ConfigCopiaAuto | null;
  /** Última copia (automática o a mano), `AAAA-MM-DD`. */
  ultima: string | null | undefined;
  /** La última copia automática no se pudo hacer. */
  fallo: boolean;
  hoy: string;
}): EstadoAncla {
  const dias = diasDesde(ultima, hoy);
  if (enNube) {
    return { nivel: 'nube', fraccion: 1, dias, sinElegir: false };
  }
  const sinElegir = config === null;
  if (dias === null) {
    return { nivel: 'mal', fraccion: 1, dias, sinElegir };
  }
  // Sin copia automática se mantiene el plazo semanal de la copia a mano.
  const plazo = config ? diasDeFrecuencia(config.frecuencia) : 7;
  const fraccion = Math.min(1, dias / plazo);
  let nivel: NivelAncla;
  if (dias > plazo + Math.max(2, plazo)) {
    nivel = 'mal';
  } else if (dias > plazo || (fallo && dias >= plazo)) {
    nivel = 'pronto';
  } else if (!config && dias >= 5) {
    nivel = 'pronto';
  } else {
    nivel = 'bien';
  }
  return { nivel, fraccion, dias, sinElegir };
}

/** Las copias que sobran (las más viejas) cuando hay más de `max`. */
export function copiasSobrantes<T extends { fecha: string }>(
  copias: readonly T[],
  max: number = MAX_COPIAS_INTERNAS,
): T[] {
  const ordenadas = [...copias].sort((a, b) =>
    a.fecha < b.fecha ? 1 : a.fecha > b.fecha ? -1 : 0,
  );
  return ordenadas.slice(max);
}

/** «rumbo-copia-casa-AAAA-MM-DD.zip»: con el nombre del presupuesto. */
export function nombreArchivoAuto(nombre: string | undefined, hoy: Date) {
  const limpio = (nombre ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30);
  return `rumbo-copia-${limpio ? `${limpio}-` : ''}${diaLocal(hoy)}.zip`;
}
